import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UTCDateRange } from '@epicuro-next/ataraxia/models';
import {
  dateTimeNow,
  getCurrentYear,
  getUTCAfternoon,
  makeInterval,
} from '@epicuro-next/utilities';
import { Interval } from 'luxon';
import { BehaviorSubject, map } from 'rxjs';

import { RangeConfig } from '../../models/range-config.type';

@Component({
  selector: 'atx-predefined-dates-picker',
  templateUrl: './predefined-dates-picker.component.html',
  styleUrls: ['./predefined-dates-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PredefinedDatesPickerComponent implements OnInit {
  @Input()
  public validDateRanges: RangeConfig[];

  public years: number[] = [];
  public currentYear$ = new BehaviorSubject<number>(getCurrentYear());
  public validPeriods$ = this.currentYear$.pipe(
    map((year) => {
      const now = dateTimeNow();
      return this.validDateRanges
        .filter((item) => {
          return (
            item.interval.start.year === year || item.interval.end.year === year
          );
        })
        .map((item) => {
          return {
            ...item,
            interval: makeInterval(
              getUTCAfternoon(item.interval.start.toISO()),
              getUTCAfternoon(item.interval.end.toISO()),
            ),
            enabled: item.interval.contains(now) || item.interval.isAfter(now),
          };
        });
    }),
  );
  public showPeriods$ = new BehaviorSubject(true);

  @Output() public periodSelect = new EventEmitter<UTCDateRange>();

  public ngOnInit() {
    this.years = [
      ...new Set(
        (this.validDateRanges ?? []).flatMap((item) => [
          item.interval.start.year,
          item.interval.end.year,
        ]),
      ),
    ].sort((a, b) => a - b);

    this.currentYear$.next(this.years[0] ?? getCurrentYear());
  }

  public dateSelected(interval: Interval): void {
    this.periodSelect.emit({
      from: getUTCAfternoon(interval.start.toISO()),
      to: getUTCAfternoon(interval.end.toISO()),
    });
  }

  public nextYear() {
    const nextYearValue = this.years.at(
      this.years.indexOf(this.currentYear$.getValue()) + 1,
    );

    if (!nextYearValue) {
      return;
    }

    this.selectYear(nextYearValue);
  }

  public previousYear() {
    const currentYearIndex = this.years.indexOf(this.currentYear$.getValue());

    if (!currentYearIndex) {
      return;
    }

    const previousYearValue = this.years.at(currentYearIndex - 1);

    if (!previousYearValue) {
      return;
    }

    this.selectYear(previousYearValue);
  }

  public selectYear(year: number) {
    this.currentYear$.next(year);
  }

  public toggleYearSelector() {
    this.showPeriods$.next(!this.showPeriods$.getValue());
  }
}
